import { z } from "zod";
import { HtsTopicKind } from "./constants";
import { HtsClassificationTopicConfig, HtsClassificationTopicConfigSchema } from "./schemas";
import { criminalChargesConvictions } from "./htsClassificationTopics/criminalChargesConvictions";
import { integrityHonesty } from "./htsClassificationTopics/integrityHonesty";
import { workplaceTrustConduct } from "./htsClassificationTopics/workplaceTrustConduct";
import { personalConductHistory } from "./htsClassificationTopics/personalConductHistory";
import { onlineSocialMediaConduct } from "./htsClassificationTopics/onlineSocialMediaConduct";
import { financialLegalCompleteness } from "./htsClassificationTopics/financialLegalCompleteness";
import { regulatoryClearanceHistory } from "./htsClassificationTopics/regulatoryClearanceHistory";
import { drivingRecordBehaviour } from "./htsClassificationTopics/drivingRecordBehaviour";
import { substanceUse } from "./htsClassificationTopics/substanceUse";
import { general } from "./htsClassificationTopics/general";
import { fitness } from "./htsClassificationTopics/fitness";
import { jobFit } from "./htsClassificationTopics/jobFit";
import { motivation } from "./htsClassificationTopics/motivation";
import { leadership } from "./htsClassificationTopics/leadership";
import { specialSkills } from "./htsClassificationTopics/specialSkills";
import { selfAwareness } from "./htsClassificationTopics/selfAwareness";

const htsClassificationTopics: HtsClassificationTopicConfig[] = [
  criminalChargesConvictions,
  integrityHonesty,
  workplaceTrustConduct,
  personalConductHistory,
  onlineSocialMediaConduct,
  financialLegalCompleteness,
  regulatoryClearanceHistory,
  drivingRecordBehaviour,
  substanceUse,
  general,
  fitness,
  jobFit,
  motivation,
  leadership,
  specialSkills,
  selfAwareness,
];

export const HTS_CLASSIFICATION_TOPICS: HtsClassificationTopicConfig[] = z
  .array(HtsClassificationTopicConfigSchema)
  .parse(htsClassificationTopics);

// Keyed by `string` rather than `HtsClassificationTopic` because lookups happen
// with a topic value read from the database, which is a nullable string —
// the `| undefined` value type forces callers to handle a miss, same
// rationale as COMPETENCY_BY_ID.
export const HTS_CLASSIFICATION_TOPIC_BY_ID: Record<string, HtsClassificationTopicConfig | undefined> =
  Object.fromEntries(HTS_CLASSIFICATION_TOPICS.map((topic) => [topic.id, topic]));

/**
 * True for a question's `classificationTopic` that should run through the
 * Section-B suitability classify/follow-up engine. A null/undefined topic
 * (any informational Section A/C question, whether or not it feeds a pillar)
 * is NOT a suitability topic. Used by htsAnswerChecks.ts's dispatch and
 * htsSubmissionIntegrityChecks.ts's perfect-person-probe filter, which must
 * never treat a Readiness answer as suitability data.
 */
export const isSuitabilityTopic = (topic: string | null | undefined): boolean =>
  topic !== null && topic !== undefined && HTS_CLASSIFICATION_TOPIC_BY_ID[topic]?.kind === HtsTopicKind.Suitability;

/**
 * True for a Section D/E question's `classificationTopic` — the ones the
 * vagueness-probe engine (htsReadinessFollowUp.ts) runs on. Says nothing
 * about scoring: pillar membership is the question's own `readinessPillars`.
 */
export const isReadinessTopic = (topic: string | null | undefined): boolean =>
  topic !== null && topic !== undefined && HTS_CLASSIFICATION_TOPIC_BY_ID[topic]?.kind === HtsTopicKind.Readiness;
